import { usePage } from '@inertiajs/vue3';
import { route } from 'ziggy-js';
import { sendToFathom } from './fathom';
import {
  IDENTIFIER_PATTERN,
  USER_EVENTS,
  type UserEventName,
  type UserEventPayloads,
} from './userEvents.generated';

/**
 * One call for every tracked event: it stores the event in `user_events` when
 * the user is signed in and the event is client-side, and forwards it to
 * Fathom under the registry's name.
 *
 * The event list, each event's properties and its Fathom name live in
 * `app/Tracking/UserEvent.php`; `userEvents.generated.ts` is its browser copy,
 * and types every call. An action that makes a request is recorded on the
 * server instead — use this only for things the server never hears about. See
 * the `tracking-user-events` skill.
 */

export interface EventSubject {
  /** A morph alias the event accepts (see its `subjects` in the registry). */
  type: string;
  uuid: string;
}

/**
 * The arguments after the name: properties are required when the event has a
 * required property, and may be left out when it has none.
 */
export type TrackArguments<N extends UserEventName> =
  Record<string, never> extends UserEventPayloads[N]
    ? [properties?: UserEventPayloads[N], subject?: EventSubject]
    : [properties: UserEventPayloads[N], subject?: EventSubject];

/** The type of one property of one event, e.g. `EventProperty<'quick_roll.used', 'type'>`. */
export type EventProperty<
  N extends UserEventName,
  K extends keyof UserEventPayloads[N],
> = UserEventPayloads[N][K];

type Values = Record<string, unknown>;

/**
 * Narrows a runtime string — a document's kind, an adapter's label — to one of
 * a property's allowed values, or null when it is not one. For values typed as
 * plain `string` by the server's data classes; a literal needs no help.
 */
export const allowedValue = <
  N extends UserEventName,
  K extends keyof UserEventPayloads[N] & string,
>(
  name: N,
  key: K,
  value: string | null | undefined
): EventProperty<N, K> | null => {
  const rule = (USER_EVENTS[name].properties as Record<string, { values?: readonly string[] }>)[
    key
  ];

  return value !== null && value !== undefined && rule?.values?.includes(value)
    ? (value as EventProperty<N, K>)
    : null;
};

interface PropertyRule {
  type: 'string' | 'int' | 'bool';
  required: boolean;
  values?: readonly string[];
  maxLength?: number;
  min?: number;
}

const problemWith = (rule: PropertyRule, value: unknown): string | null => {
  const typed =
    rule.type === 'string'
      ? typeof value === 'string'
      : rule.type === 'int'
        ? Number.isInteger(value)
        : typeof value === 'boolean';

  if (!typed) {
    return 'type';
  }

  if (rule.values !== undefined) {
    return rule.values.includes(value as string) ? null : 'domain';
  }

  if (typeof value === 'string') {
    if (rule.maxLength !== undefined && value.length > rule.maxLength) {
      return 'length';
    }

    if (!IDENTIFIER_PATTERN.test(value)) {
      return 'format';
    }
  }

  if (typeof value === 'number' && rule.min !== undefined && value < rule.min) {
    return 'range';
  }

  return null;
};

/**
 * The same checks the server makes, run before anything leaves the page. The
 * compiler catches most mistakes; this catches the rest (stale code, `any`,
 * values built at runtime), so a bad value never becomes a Fathom event name.
 * Returns problem keys and reasons only — never values.
 */
export const eventProblems = (
  name: UserEventName,
  properties: Values = {},
  subject?: EventSubject
): Record<string, string> => {
  const event = USER_EVENTS[name];
  const rules = event.properties as Record<string, PropertyRule>;
  const problems: Record<string, string> = {};

  for (const [key, rule] of Object.entries(rules)) {
    if (properties[key] === undefined) {
      if (rule.required) {
        problems[key] = 'missing';
      }

      continue;
    }

    const problem = problemWith(rule, properties[key]);

    if (problem !== null) {
      problems[key] = problem;
    }
  }

  for (const key of Object.keys(properties)) {
    if (!(key in rules)) {
      problems[key] = 'unknown';
    }
  }

  if (subject && !(event.subjects as readonly string[]).includes(subject.type)) {
    problems.subject = 'not_accepted';
  }

  return problems;
};

/**
 * Fills a Fathom template's `{placeholders}` from the event's properties. Null
 * when the event has no Fathom name or a placeholder has no value — sending a
 * half-filled name would file the event under a name nobody queries.
 */
export const fathomName = (name: UserEventName, properties: Values = {}): string | null => {
  const template = USER_EVENTS[name].fathom;

  if (template === null) {
    return null;
  }

  let missing = false;

  const filled = template.replace(/\{(\w+)\}/g, (_match, key: string) => {
    const value = properties[key];

    if (value === undefined) {
      missing = true;
      return '';
    }

    return String(value);
  });

  return missing ? null : filled;
};

const xsrfToken = (): string | undefined => {
  const match = document.cookie.match(/(?:^|;\s*)XSRF-TOKEN=([^;]+)/);

  return match ? decodeURIComponent(match[1]) : undefined;
};

/**
 * `keepalive` lets the request outlive the page, so an event fired right before
 * a navigation still arrives. Failures are ignored: a lost event must never
 * surface to the user.
 */
const store = (name: UserEventName, properties: Values, subject?: EventSubject): void => {
  const token = xsrfToken();

  fetch(route('v1.events.store'), {
    method: 'POST',
    keepalive: true,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
      ...(token ? { 'X-XSRF-TOKEN': token } : {}),
    },
    body: JSON.stringify({ name, properties, ...(subject ? { subject } : {}) }),
  }).catch(() => {});
};

export const track = <N extends UserEventName>(name: N, ...args: TrackArguments<N>): void => {
  if (typeof window === 'undefined') {
    return;
  }

  const properties = (args[0] ?? {}) as Values;
  const subject = args[1];
  const problems = eventProblems(name, properties, subject);

  if (Object.keys(problems).length > 0) {
    // Keys and reasons only: a value that failed is where free text would be.
    // eslint-disable-next-line no-console
    console.debug(`[Tracking] ${name} not tracked`, problems);
    return;
  }

  const event = USER_EVENTS[name];
  const signedIn = Boolean(usePage().props?.auth?.user);

  if (event.side === 'client' && signedIn) {
    store(name, properties, subject);
  }

  const fathom = fathomName(name, properties);

  if (fathom !== null) {
    const value = properties.value_cents;

    sendToFathom(fathom, typeof value === 'number' ? value : undefined);
  }
};

export const useTracking = () => ({ track });
